import React, {useState} from 'react'
import {Button} from './Button'
import {ArrowRight, CheckIcon} from './icons'
import {signupModalHref} from './constants'
import briefcaseIcon from '../../images/icons/briefcase.png'
import documentTrayIcon from '../../images/icons/document-tray.png'
import lifebuoyIcon from '../../images/icons/lifebuoy.png'
import megaphoneIcon from '../../images/icons/megaphone.png'
import sparklesIcon from '../../images/icons/sparkles.svg'
import usersIcon from '../../images/icons/users.png'

const pricingCategories = [
  {
    group: 'Pre-Market',
    icon: sparklesIcon,
    items: [
      {label: 'Easy TC / assistant access'},
      {label: 'Seller disclosures form filling'},
      {label: 'Create comprehensive Listing Information Packages'},
      {label: 'Scan documents', pro: true},
      {label: 'Save custom document templates', pro: true},
    ],
  },
  {
    group: 'On-Market',
    icon: megaphoneIcon,
    items: [
      {label: 'Share unlimited Listing Information Packages'},
      {label: 'Sharable link for marketing materials'},
      {label: 'Buyer Interest Reports (activity tracking)', pro: true},
      {label: 'Broadcast updates to viewers', pro: true},
      {label: 'Blind copy mass messaging to clients', pro: true},
    ],
  },
  {
    group: 'Offers',
    icon: documentTrayIcon,
    items: [
      {label: 'Receive offers'},
      {label: 'Submit offers'},
      {label: 'Accept offers'},
      {label: 'Automatic offer terms comparison sheet'},
      {label: 'Custom offers dashboard for sellers', pro: true},
    ],
  },
  {
    group: 'Administrative',
    icon: briefcaseIcon,
    items: [
      {label: 'Unlimited storage (no fees)'},
      {label: 'Easy TC / assistant access'},
      {label: 'DocuSign integration'},
      {label: 'Easy export of all data & contacts'},
      {label: 'Custom branding', pro: true},
    ],
  },
  {
    group: 'Support',
    icon: lifebuoyIcon,
    items: [{label: 'Help center guides and training'}, {label: '1-on-1 customer support', pro: true}, {label: 'Team training', pro: true}],
  },
  {
    group: 'Agent & Client Services',
    icon: usersIcon,
    items: [{label: 'Buy Before You Sell™'}, {label: 'Closing Services'}, {label: 'Agent approval required', note: true}],
  },
]

function UserIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
    </svg>
  )
}

function ZapIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="13 2 3 14 11 14 9 22 21 10 13 10" />
    </svg>
  )
}

function PricingPlan({pro}) {
  const [billing, setBilling] = useState('monthly')
  const isYearly = pro && billing === 'yearly'
  return (
    <div className={`hlm-plan ${pro ? 'is-pro' : ''}`}>
      <div className="hlm-plan-head">
        <div className="hlm-plan-name">
          <span className={`hlm-plan-icon ${pro ? 'is-pro' : ''}`}>{pro ? <ZapIcon /> : <UserIcon />}</span>
          {pro ? 'Pro' : 'Basic'}
          {pro && (
            <div className="hlm-billing-toggle" role="group" aria-label="Billing period">
              <button
                type="button"
                className={`hlm-billing-opt ${billing === 'monthly' ? 'is-active' : ''}`}
                aria-pressed={billing === 'monthly'}
                onClick={() => setBilling('monthly')}>
                Monthly
              </button>
              <button
                type="button"
                className={`hlm-billing-opt ${billing === 'yearly' ? 'is-active' : ''}`}
                aria-pressed={billing === 'yearly'}
                onClick={() => setBilling('yearly')}>
                Yearly
                <span className="hlm-billing-save">15% off</span>
              </button>
            </div>
          )}
        </div>
        <div className="hlm-plan-price">
          <strong>{pro ? (isYearly ? '$33' : '$39') : '$0'}</strong>
          <span>
            {pro ? (
              isYearly ? (
                <>
                  / month <span className="hlm-plan-billed-note">($399 billed annually)</span>
                </>
              ) : (
                '/ month'
              )
            ) : (
              '/ forever'
            )}
          </span>
        </div>
        <p>
          {pro
            ? 'For full-time agents. Everything in Basic plus tracking, branding, and 1-on-1 support.'
            : 'Free for every listing. Disclosures, packages, and offer management without limits.'}
        </p>
        <Button href={signupModalHref} variant={pro ? 'primary' : 'dark'} full>
          {pro ? 'Try 14 days free' : 'Get started free'}
          <ArrowRight />
        </Button>
      </div>
      <div className="hlm-plan-features">
        {pricingCategories.map(category => (
          <div className="hlm-plan-cat" key={category.group}>
            <h4>
              <span className="hlm-plan-cat-icon">
                <img src={category.icon} alt="" aria-hidden="true" />
              </span>
              {category.group}
            </h4>
            <ul>
              {category.items.map(item => {
                if (item.note) {
                  return (
                    <li key={item.label} className="is-note">
                      {item.label}
                    </li>
                  )
                }
                const isProItem = !!item.pro
                const className = isProItem && pro ? 'is-pro' : isProItem ? 'is-off' : ''
                return (
                  <li key={item.label} className={className}>
                    <span className="hlm-plan-check">
                      {isProItem && !pro ? <span className="hlm-plan-dash" aria-hidden="true" /> : <CheckIcon size={13} stroke={3} />}
                    </span>
                    <span>{item.label}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function PricingV2() {
  return (
    <section id="pricing" className="hlm-pricing">
      <div className="hlm-container">
        <div className="hlm-center">
          <div className="hlm-features-eyebrow">Pricing</div>
          <h2 className="hlm-h2">Simple pricing that scales with your business.</h2>
          <p className="hlm-lede hlm-lede-center">Free to start. Upgrade when you need more power. No contracts, no per-listing fees, no surprises.</p>
        </div>
        <div className="hlm-pricing-grid">
          <PricingPlan />
          <PricingPlan pro />
        </div>
        <p className="hlm-pricing-foot">
          Some Agent &amp; Client Services may require approval. Switch plans anytime — cancel Pro and keep working with your listings, packages, and history
          intact.
        </p>
      </div>
    </section>
  )
}

export default PricingV2
